import {
  CommentOutlined,
  EditOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Addon, Item, Modifier } from "../../../../api/menu";
import { getItemName, getTranslation } from "../../../../utils/translations";
import { LunchTag } from "../../lunch/LunchTag";
import type { OrderRow } from "./orderSummaryUtils";
import {
  KitchenHoldRowChip,
  KitchenHoldRowPicker,
  type KitchenHoldRowProps,
} from "./KitchenHoldRow";

/** Показва количество без излишни нули, до 3 знака — колкото приема модалът
 *  „Промени" (3 → "3", 0.5 → "0.5", 0.543 → "0.543"). Закръгляне до 1 знак
 *  тук показваше 0.543 като "0.5" — лъжеше за реалното количество. */
const formatQuantity = (quantity: number): string =>
  String(parseFloat(quantity.toFixed(3)));

interface OrderPanelRowProps {
  row: OrderRow;
  /** Продуктът на реда (вече намерен в каталога). */
  item: Item;
  allItems: Item[];
  allAddons: Addon[];
  allModifiers: Modifier[];
  /** Цветовете на лентата отляво (станции или група — `colorBy`). */
  stripes: string[];
  isSheet: boolean;
  /** Редът съдържа последно маркирания запис — към него скролира прегледът. */
  isLast: boolean;
  onItemEdit: OrderPanelRowEdit;
  onQuantityChange: (index: number, delta: number) => void;
  /** Времето на реда за кухнята („Сега", минути, „Ръчно"); null — без него
   *  (локален режим: без интернет задържане няма). `compact` — тесният панел. */
  kitchenHold: (KitchenHoldRowProps & { compact: boolean }) | null;
}

type OrderPanelRowEdit = (indices: number[], orderItem: OrderRow["orderItem"]) => void;

/**
 * Ред от „Преглед на поръчката" (`OrderPanelContent`): лента в цвета на
 * станцията/групата, молив + име + сума, добавките, модификаторите и
 * коментарът на свои редове, отдолу количество × цена, времето за кухнята
 * и степерът. Кликът върху времето разгъва избора под реда.
 */
export const OrderPanelRow = ({
  row,
  item,
  allItems,
  allAddons,
  allModifiers,
  stripes,
  isSheet,
  isLast,
  onItemEdit,
  onQuantityChange,
  kitchenHold,
}: OrderPanelRowProps) => {
  const { t, i18n } = useTranslation();
  const orderItem = row.orderItem;
  const itemName = getItemName(item.itemLangs, i18n.language, item.slug);
  const quantity = row.quantity;
  const price = item.price;

  // Добавки: totalQty е ФИНАЛНИЯТ брой — тип "1" (обикновена) е с абсолютно
  // количество от брояча в модала (не зависи от бройката на продукта) → × броя
  // слети записи; тип "2"/"3" са по 1 на продукт → × сборното quantity.
  const addons: { name: string; price: number; totalQty: number }[] = [];
  Object.entries(orderItem.addons).forEach(([addonId, qtyMap]) => {
    const addon = allAddons.find((a) => a.id === Number(addonId));
    if (!addon) return;
    const absolute = addon.type === 1;
    Object.entries(qtyMap).forEach(([addonItemId, addonQty]) => {
      const itemId = Number(addonItemId);
      const addonItemData = addon.items.find((ai) => ai.itemId === itemId);
      const addonItemPrice =
        addonItemData?.addonItemPrice ?? addonItemData?.price ?? 0;
      const addonItem = allItems.find((i) => i.id === itemId);
      const addonName = addonItem
        ? getItemName(addonItem.itemLangs, i18n.language, addonItem.slug)
        : `#${itemId}`;
      addons.push({
        name: addonName,
        price: addonItemPrice,
        totalQty: absolute ? addonQty * row.indices.length : addonQty * quantity,
      });
    });
  });

  const addonsLineTotal = addons.reduce(
    (sum, a) => sum + a.price * a.totalQty,
    0,
  );
  const totalItemPrice = price * quantity + addonsLineTotal;

  // Модификатори (по име) — показват се като chips до коментара
  const modifiers: string[] = orderItem.modifiers
    .map((modId) => {
      const modifier = allModifiers.find((m) => m.id === modId);
      return modifier
        ? getTranslation(modifier.translations, i18n.language, modifier.slug)
        : "";
    })
    .filter(Boolean);

  return (
    <div
      data-order-last={isLast ? "" : undefined}
      // shrink-0: overflow-hidden сваля min-height на flex item-а до 0
      // и редовете се свиваха вместо списъкът да скролира
      className={`${
        isSheet ? "bg-pos-panel rounded-[11px]" : "bg-pos-card rounded-[10px]"
      } border border-pos-line3 flex shrink-0 overflow-hidden`}
    >
      {/* Лента отляво в цвета на станцията/групата — при няколко
          станции всяка е равна част (цветовете са от базата →
          inline style) */}
      {stripes.length > 0 && (
        <span aria-hidden className="w-1 shrink-0 self-stretch flex flex-col">
          {stripes.map((color, stripeIndex) => (
            <span
              key={stripeIndex}
              className="flex-1"
              style={{ background: color }}
            />
          ))}
        </span>
      )}
      <div
        className={`flex-1 min-w-0 flex flex-col gap-1.5 ${
          isSheet ? "px-3 py-2.5" : "px-[11px] py-[9px]"
        }`}
      >
        {/* Ред 1: молив (коментар към продукта) + име + сума на реда */}
        <div className="flex items-center gap-1.5 min-w-0">
          <button
            type="button"
            title={t("ordering.itemComment")}
            onClick={() => onItemEdit(row.indices, orderItem)}
            className={`shrink-0 border-0 bg-transparent p-0 grid place-items-center ${isSheet ? "text-[13px]" : "text-[15px]"} cursor-pointer ${
              orderItem.comment || modifiers.length > 0
                ? "text-pos-accent"
                : "text-pos-faint"
            }`}
          >
            <EditOutlined />
          </button>
          <span
            title={itemName}
            className={`flex-1 min-w-0 font-semibold text-pos-ink truncate ${isSheet ? "text-[13px]" : "text-[15px] leading-[19.5px]"}`}
          >
            {itemName}
          </span>
          {item.lunchMenuId != null && <LunchTag />}
          <span
            className={`font-pos-mono font-bold text-pos-ink shrink-0 ${isSheet ? "text-[13px]" : "text-[15px] leading-[19.5px]"}`}
          >
            {totalItemPrice.toFixed(2)}
          </span>
        </div>

        {/* Добавките — на собствен ред от chips */}
        {addons.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {addons.map((a, idx) => (
              <span
                key={`a${idx}`}
                className={`inline-flex items-center gap-1 bg-pos-addon-bg border border-pos-addon-brd text-pos-addon-ink rounded-[7px] px-1.5 py-0.5 ${isSheet ? "text-[11px]" : "text-[13px] leading-[16.5px]"}`}
              >
                <span className="text-pos-disc-ink">＋</span>
                {a.name}
                <span className="font-pos-mono">
                  {formatQuantity(a.totalQty)}×{a.price.toFixed(2)}
                </span>
              </span>
            ))}
          </div>
        )}

        {/* Модификаторите — на собствен ред, червени като коментара;
          не се режат — пренасят се на нов ред при нужда */}
        {modifiers.length > 0 && (
          <div
            className={`min-w-0 font-semibold italic text-pos-danger-ink break-words ${isSheet ? "text-[12.5px]" : "text-[14.5px] leading-[19px]"}`}
          >
            {modifiers.join(" · ")}
          </div>
        )}

        {/* Коментарът към продукта — собствен ред, червен и четим
          (същият стил като в детайла на масата) */}
        {orderItem.comment && (
          <div
            className={`flex items-start gap-1.5 min-w-0 font-semibold italic text-pos-danger-ink ${isSheet ? "text-[13px]" : "text-[15px] leading-[19.5px]"}`}
          >
            <CommentOutlined className="text-xs shrink-0 mt-1" />
            <span className="min-w-0 break-words">{orderItem.comment}</span>
          </div>
        )}

        {/* Ред 3: количество × цена + времето за кухнята + малък степер */}
        <div className={`flex items-center ${kitchenHold?.compact ? "gap-1.5" : "gap-2"}`}>
          <span
            className={`font-pos-mono text-pos-muted flex-1 ${isSheet ? "text-[11px]" : "text-[13px] leading-[16.5px]"}`}
          >
            {formatQuantity(quantity)} × {price.toFixed(2)}
          </span>
          {kitchenHold && (
            <KitchenHoldRowChip
              time={kitchenHold.time}
              pickerOpen={kitchenHold.pickerOpen}
              onTogglePicker={kitchenHold.onTogglePicker}
              compact={kitchenHold.compact}
            />
          )}
          <QuantityStepper
            indices={row.indices}
            quantity={quantity}
            isSheet={isSheet}
            onQuantityChange={onQuantityChange}
          />
        </div>

        {kitchenHold?.pickerOpen && (
          <KitchenHoldRowPicker
            time={kitchenHold.time}
            onPick={kitchenHold.onPick}
            onApplyToAll={kitchenHold.onApplyToAll}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Малък степер за редовете в прегледа (32×32, рамкирана група). При обобщен
 * ред промяната отива в ПОСЛЕДНИЯ от слетите записи — така „−" маха цял
 * запис, когато той стигне 0, а сборът на реда пада точно с една бройка.
 */
const QuantityStepper = ({
  indices,
  quantity,
  isSheet,
  onQuantityChange,
}: {
  indices: number[];
  quantity: number;
  isSheet: boolean;
  onQuantityChange: (index: number, delta: number) => void;
}) => {
  const targetIndex = indices[indices.length - 1];
  return (
    <span className="inline-flex items-stretch border border-pos-ctl rounded-[9px] overflow-hidden bg-pos-card shrink-0">
      <button
        type="button"
        onClick={() => onQuantityChange(targetIndex, -1)}
        className="w-8 h-8 bg-pos-stepper text-pos-dim text-[17px] leading-none grid place-items-center cursor-pointer"
      >
        <MinusOutlined className="text-[14px]" />
      </button>
      <span
        className={`min-w-[26px] px-1 grid place-items-center font-pos-mono font-semibold text-pos-ink ${isSheet ? "text-sm" : "text-base"}`}
      >
        {formatQuantity(quantity)}
      </span>
      <button
        type="button"
        onClick={() => onQuantityChange(targetIndex, 1)}
        className="w-8 h-8 bg-pos-stepper text-pos-accent-ink text-[17px] leading-none grid place-items-center cursor-pointer"
      >
        <PlusOutlined className="text-[14px]" />
      </button>
    </span>
  );
};
